// WhatsApp OTP Service
// Handles OTP generation, sending via WhatsApp, and verification

import whatsappService from './communication/whatsappService';

class WhatsAppOTPService {
  constructor() {
    this.otpStore = new Map(); // In-memory OTP storage (consider using Firebase in production)
    this.otpExpiryTime = 5 * 60 * 1000; // 5 minutes
    this.maxAttempts = 3;
    console.log('🔐 WhatsApp OTP Service initialized');
  }

  /**
   * Generate a 6-digit OTP
   */
  generateOTP() {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  /**
   * Send OTP to customer via WhatsApp
   * @param {string} phoneNumber - Customer phone number
   * @param {string} customerName - Customer name (optional)
   */
  async sendOTP(phoneNumber, customerName = '') {
    try {
      // Format phone number
      const formattedPhone = this._formatPhoneNumber(phoneNumber);

      // Generate OTP
      const otp = this.generateOTP();

      // Store OTP with metadata
      this.otpStore.set(formattedPhone, {
        otp,
        attempts: 0,
        createdAt: Date.now(),
        expiresAt: Date.now() + this.otpExpiryTime
      });

      // Create WhatsApp message
      const message = this._formatOTPMessage(otp, customerName);

      // Send via WhatsApp service
      const result = await whatsappService.sendMessage(
        formattedPhone,
        message,
        'otp_verification'
      );

      if (result.success) {
        console.log(`✅ OTP sent to ${formattedPhone}`);
        return {
          success: true,
          message: 'OTP sent successfully via WhatsApp',
          expiresIn: this.otpExpiryTime / 1000 // in seconds
        };
      } else {
        // If WhatsApp fails, return browser WhatsApp URL
        if (result.provider === 'browser') {
          return {
            success: true,
            requiresManualAction: true,
            url: result.url,
            message: 'Please click the link to send OTP via WhatsApp',
            otp // For demo purposes, return OTP in dev mode
          };
        }

        throw new Error('Failed to send OTP via WhatsApp');
      }
    } catch (error) {
      console.error('❌ Error sending OTP:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Verify OTP entered by customer
   * @param {string} phoneNumber - Customer phone number
   * @param {string} enteredOTP - OTP entered by customer
   */
  verifyOTP(phoneNumber, enteredOTP) {
    try {
      const formattedPhone = this._formatPhoneNumber(phoneNumber);

      const otpData = this.otpStore.get(formattedPhone);

      if (!otpData) {
        return {
          success: false,
          error: 'OTP not found. Please request a new OTP.'
        };
      }

      // Check if OTP expired
      if (Date.now() > otpData.expiresAt) {
        this.otpStore.delete(formattedPhone);
        return {
          success: false,
          error: 'OTP expired. Please request a new OTP.'
        };
      }

      // Check max attempts
      if (otpData.attempts >= this.maxAttempts) {
        this.otpStore.delete(formattedPhone);
        return {
          success: false,
          error: 'Maximum attempts exceeded. Please request a new OTP.'
        };
      }

      // Increment attempts
      otpData.attempts++;

      // Verify OTP
      if (otpData.otp === enteredOTP.trim()) {
        this.otpStore.delete(formattedPhone); // Clear OTP after successful verification
        console.log(`✅ OTP verified for ${formattedPhone}`);
        return {
          success: true,
          message: 'Phone number verified successfully!'
        };
      } else {
        const remainingAttempts = this.maxAttempts - otpData.attempts;
        return {
          success: false,
          error: `Invalid OTP. ${remainingAttempts} attempt(s) remaining.`
        };
      }
    } catch (error) {
      console.error('❌ Error verifying OTP:', error);
      return {
        success: false,
        error: 'Verification failed. Please try again.'
      };
    }
  }

  /**
   * Resend OTP to customer
   * @param {string} phoneNumber - Customer phone number
   * @param {string} customerName - Customer name (optional)
   */
  async resendOTP(phoneNumber, customerName = '') {
    // Clear existing OTP
    const formattedPhone = this._formatPhoneNumber(phoneNumber);
    this.otpStore.delete(formattedPhone);

    // Send new OTP
    return await this.sendOTP(phoneNumber, customerName);
  }

  /**
   * Format OTP message for WhatsApp
   */
  _formatOTPMessage(otp, customerName) {
    const greeting = customerName ? `Hi ${customerName}! 👋\n\n` : 'Hi! 👋\n\n';

    return (
      `${greeting}` +
      `Your Odisha Zaika verification code is:\n\n` +
      `*${otp}*\n\n` +
      `This code will expire in 5 minutes.\n\n` +
      `⚠️ Do not share this code with anyone.\n\n` +
      `If you didn't request this code, please ignore this message.`
    );
  }

  /**
   * Format phone number to E.164 format
   */
  _formatPhoneNumber(phone) {
    // Remove all non-digits
    let cleaned = phone.replace(/\D/g, '');

    // If starts with 91, ensure + prefix
    if (cleaned.startsWith('91')) {
      return '+' + cleaned;
    }

    // If 10 digits, add +91 prefix
    if (cleaned.length === 10) {
      return '+91' + cleaned;
    }

    // Otherwise return as-is with + prefix
    return '+' + cleaned;
  }

  /**
   * Clean up expired OTPs (run periodically)
   */
  cleanupExpiredOTPs() {
    const now = Date.now();
    for (const [phone, data] of this.otpStore.entries()) {
      if (now > data.expiresAt) {
        this.otpStore.delete(phone);
        console.log(`🧹 Cleaned up expired OTP for ${phone}`);
      }
    }
  }
}

// Singleton instance
const whatsappOtpService = new WhatsAppOTPService();

// Cleanup expired OTPs every 5 minutes
setInterval(() => {
  whatsappOtpService.cleanupExpiredOTPs();
}, 5 * 60 * 1000);

export default whatsappOtpService;
